import { validateMatchSnapshot, normalizeSnapshot } from "./match-schema.js";
import { cloneMatchForLocalSession } from "./session-snapshot.js";

export const MAX_RECOVERY_CHECKPOINTS = 12;
export const RECOVERY_KEY_PREFIX = "cs_recovery_v1:";
export const LAST_RECOVERY_MATCH_KEY = "cs_recovery_last_match_v1";

function cloneMatchForRecovery(match) {
  return cloneMatchForLocalSession(match, 6);
}

export function checkpointSummary(checkpoint) {
  return checkpoint?.summary || null;
}

export function createCheckpoint(match, now = Date.now()) {
  const snapshot = normalizeSnapshot(cloneMatchForRecovery(match));
  const checked = validateMatchSnapshot(snapshot);
  if (!checked.ok) return null;
  const innings = checked.value.innings?.at(-1);
  const lastBall = innings?.ballLog?.at(-1);
  if (!innings) return null;
  return {
    version: 1,
    createdAt: now,
    matchId: checked.value.id,
    stateKey: [checked.value.innings.length, innings.balls, innings.ballLog.length, innings.totalRuns, innings.wickets, innings.currentStriker, innings.currentNonStriker, innings.currentBowler, checked.value.result ? "result" : "live"].join(":"),
    summary: {
      teams: `${checked.value.teamA.name} vs ${checked.value.teamB.name}`,
      innings: checked.value.innings.length,
      score: `${innings.totalRuns}/${innings.wickets}`,
      balls: innings.balls,
      lastBall: lastBall?.text || lastBall?.type || "Match started"
    },
    snapshot: checked.value
  };
}

export function appendCheckpoint(list, checkpoint, max = MAX_RECOVERY_CHECKPOINTS) {
  if (!checkpoint) return Array.isArray(list) ? list.slice(-max) : [];
  const current = Array.isArray(list) ? list : [];
  if (current.at(-1)?.stateKey === checkpoint.stateKey) return [...current.slice(0, -1), checkpoint].slice(-max);
  return [...current, checkpoint].slice(-max);
}

export function findLatestValidCheckpoint(list) {
  const checkpoints = Array.isArray(list) ? list : [];
  for (let i = checkpoints.length - 1; i >= 0; i--) {
    const checked = validateMatchSnapshot(normalizeSnapshot(checkpoints[i]?.snapshot));
    if (checked.ok) return { ...checkpoints[i], snapshot: checked.value };
  }
  return null;
}

export function restoreCheckpoint(checkpoint) {
  const checked = validateMatchSnapshot(normalizeSnapshot(checkpoint?.snapshot));
  return checked.ok ? checked.value : null;
}

export function selectRecoveryState(activeInput, checkpoints) {
  const active = validateMatchSnapshot(normalizeSnapshot(activeInput));
  const validCheckpoints = (Array.isArray(checkpoints) ? checkpoints : []).filter(item => validateMatchSnapshot(normalizeSnapshot(item?.snapshot)).ok);
  const latest = validCheckpoints.at(-1) || null;
  const previous = validCheckpoints.length > 1 ? validCheckpoints.at(-2) : null;
  if (active.ok) return { match: active.value, restored: false, warning: "", latest, previous };
  const fallback = findLatestValidCheckpoint(checkpoints);
  if (fallback) {
    return { match: restoreCheckpoint(fallback), restored: true, warning: "Active match was damaged. Restored the latest safe checkpoint.", latest: fallback, previous };
  }
  return { match: null, restored: false, warning: "Match could not be recovered because no valid checkpoint was found.", latest: null, previous: null };
}

export function recoveryStorageKey(matchId) {
  return RECOVERY_KEY_PREFIX + String(matchId || "");
}

/**
 * Promise-wrapped IndexedDB put.
 *
 * A plain synchronous try/catch around `store.put()` catches almost nothing:
 * IndexedDB reports quota exhaustion and transaction aborts ASYNCHRONOUSLY via
 * onerror/onabort. The old code therefore reported success on every failed
 * write, so a full device silently stopped saving recovery checkpoints while
 * the app carried on as if the match were protected.
 */
export function idbPut(db, storeName, value, key) {
  return new Promise((resolve, reject) => {
    let tx;
    try {
      tx = db.transaction(storeName, "readwrite");
    } catch (e) {
      reject(e);
      return;
    }
    try {
      if (key === undefined) tx.objectStore(storeName).put(value);
      else tx.objectStore(storeName).put(value, key);
    } catch (e) {
      reject(e);
      return;
    }
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error || new Error("IndexedDB write failed"));
    tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction aborted"));
  });
}

export async function readRecoveryCheckpoints(db, matchId) {
  if (!db || !matchId) return [];
  if (typeof db.transaction === "function") {
    try {
      return await new Promise(resolve => {
        const req = db.transaction("checkpoints", "readonly").objectStore("checkpoints").get(matchId);
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => resolve([]);
      });
    } catch (_) {
      return [];
    }
  }
  try {
    const raw = db.getItem(recoveryStorageKey(matchId));
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

export async function writeRecoveryCheckpoint(db, match) {
  const checkpoint = createCheckpoint(match);
  if (!db || !checkpoint) return null;
  const list = appendCheckpoint(await readRecoveryCheckpoints(db, match.id), checkpoint);
  if (typeof db.transaction === "function") {
    try {
      await idbPut(db, "checkpoints", list, match.id);
    } catch (e) {
      // Surface the failure — the caller must NOT believe the match is protected.
      console.error("IndexedDB checkpoint write failed:", e);
      return null;
    }
  } else {
    try {
      db.setItem(recoveryStorageKey(match.id), JSON.stringify(list));
    } catch (e) {
      console.error("Storage checkpoint write failed:", e);
      return null;
    }
  }
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LAST_RECOVERY_MATCH_KEY, match.id);
    } else if (typeof db.setItem === "function") {
      db.setItem(LAST_RECOVERY_MATCH_KEY, match.id);
    }
  } catch (_) {}
  return checkpoint;
}

export async function writeRecoveryCheckpointList(db, matchId, list) {
  if (!db || !matchId || !Array.isArray(list)) return false;
  if (typeof db.transaction === "function") {
    try {
      await idbPut(db, "checkpoints", list, matchId);
    } catch (e) {
      console.error("IndexedDB checkpoint list write failed:", e);
      return false;
    }
  } else {
    try {
      db.setItem(recoveryStorageKey(matchId), JSON.stringify(list));
    } catch (e) {
      console.error("Storage checkpoint list write failed:", e);
      return false;
    }
  }
  return true;
}

export async function clearRecoveryCheckpoints(db, matchId) {
  if (!db || !matchId) return;
  if (typeof db.transaction === "function") {
    try {
      db.transaction("checkpoints", "readwrite").objectStore("checkpoints").delete(matchId);
    } catch (_) {}
  } else {
    try {
      db.removeItem(recoveryStorageKey(matchId));
    } catch (_) {}
  }
}
